import React, { useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserEntity, UserFormProps } from "../types/user.types";
import { userSchema, UserFormData } from "../../../schemas/forms";
import {
  ControlledTextField,
  ControlledSelect,
} from "../../../components/form";
import { useGet } from "../../../packages/hooks";
import { useEffectiveBranchId } from "../../../hooks/use-effective-branch-id";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import {
  ROLE_OPTIONS,
  PASSWORD_EDIT_HELP_TEXT,
  PASSWORD_PLACEHOLDER_EDIT,
  PASSWORD_PLACEHOLDER_CREATE,
  CONFIRM_PASSWORD_PLACEHOLDER_EDIT,
  CONFIRM_PASSWORD_PLACEHOLDER_CREATE,
  BRANCH_SELECT_PLACEHOLDER,
} from "../constants/user-form.constants";

export const UserForm: React.FC<UserFormProps> = ({
  onSubmit,
  initialValues,
  isLoading = false,
}) => {
  const effectiveBranchId = useEffectiveBranchId();

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: initialValues?.email || "",
      password: "",
      confirmPassword: "",
      role: initialValues?.role || "ADMIN",
      branchId: initialValues?.branchId || effectiveBranchId || "",
    },
  });

  // Update branchId when effectiveBranchId changes
  useEffect(() => {
    if (effectiveBranchId && !initialValues?.branchId) {
      form.setValue("branchId", effectiveBranchId);
    }
  }, [effectiveBranchId, form, initialValues?.branchId]);

  const handleFormSubmit = async (data: UserFormData): Promise<void> => {
    const userData: Partial<UserEntity> = {
      ...(data.email && { email: data.email }),
      ...(data.password && { password: data.password }),
      role: data.role,
      ...(data.branchId && { branchId: data.branchId }),
    };

    await onSubmit(userData);
  };

  const { data: branchesData } = useGet<any>(API_ENDPOINTS.BRANCHES.LIST, {
    queryParams: { limit: 100 },
  });

  const branchOptions = useMemo(() => {
    let branches = [];

    if (branchesData?.branches) {
      branches = branchesData.branches;
    } else if (branchesData?.data?.branches) {
      branches = branchesData.data.branches;
    } else if (Array.isArray(branchesData)) {
      branches = branchesData;
    }

    return [
      { value: "", label: BRANCH_SELECT_PLACEHOLDER },
      ...branches.map((branch: any) => ({
        value: branch.id,
        label: branch.name,
      })),
    ];
  }, [branchesData]);

  return (
    <form
      onSubmit={form.handleSubmit(handleFormSubmit)}
      className="form-space-y-2"
    >
      <div className="form-section">
        <h3 className="form-section-title">Основная информация</h3>

        <ControlledTextField
          control={form.control}
          name="email"
          label="Email"
          placeholder="user@example.com"
          type="email"
          disabled={isLoading}
        />

        <ControlledSelect
          control={form.control}
          name="role"
          label="Роль"
          options={ROLE_OPTIONS}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Пароль</h3>
        {initialValues ? (
          <p
            style={{ fontSize: "0.85rem", color: "#666", marginBottom: "12px" }}
          >
            {PASSWORD_EDIT_HELP_TEXT}
          </p>
        ) : null}

        <ControlledTextField
          control={form.control}
          name="password"
          label="Пароль"
          placeholder={
            initialValues
              ? PASSWORD_PLACEHOLDER_EDIT
              : PASSWORD_PLACEHOLDER_CREATE
          }
          type="password"
          required={!initialValues}
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="confirmPassword"
          label="Подтвердите пароль"
          placeholder={
            initialValues
              ? CONFIRM_PASSWORD_PLACEHOLDER_EDIT
              : CONFIRM_PASSWORD_PLACEHOLDER_CREATE
          }
          type="password"
          required={!initialValues}
          disabled={isLoading}
        />
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Дополнительная информация</h3>

        <ControlledSelect
          control={form.control}
          name="branchId"
          label="Филиал"
          options={branchOptions}
          disabled={isLoading}
        />
      </div>
      <div className="form-wrapper__actions">
        <button
          type="submit"
          className="form-button form-button--primary form-button--full"
          disabled={isLoading}
        >
          {isLoading
            ? "Загрузка..."
            : initialValues
            ? "Сохранить изменения"
            : "Добавить пользователя"}
        </button>
      </div>
    </form>
  );
};
